import React from "react";
import usePageMeta from "../../utils/usePageMeta";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ContactUs } from "../../constant/data.js";
import ContactForm from "../../components/ContactForm";
import { FaLinkedinIn, FaGithub } from "react-icons/fa";
import { FaSquareUpwork } from "react-icons/fa6";
import { TbBrandFiverr } from "react-icons/tb";

import {
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineLocationMarker,
} from "react-icons/hi";

// Long addresses break before the "@" instead of mid-word.
const breakEmail = (text) => {
  if (typeof text !== "string" || !text.includes("@")) return text;
  const [local, domain] = text.split("@");
  return (
    <>
      {local}
      <wbr />
      {"@" + domain}
    </>
  );
};

const iconMap = {
  Upwork: FaSquareUpwork,
  Fiverr: TbBrandFiverr,
  LinkedIn: FaLinkedinIn,
  GitHub: FaGithub,
  Email: HiOutlineMail,
  Phone: HiOutlinePhone,
  Address: HiOutlineLocationMarker,
};

const Tile = ({ entry, idx }) => {
  const Icon = iconMap[entry.title] || HiOutlineMail;
  const Tag = entry.link ? "a" : "div";
  const linkProps = entry.link
    ? entry.link.startsWith("mailto:") || entry.link.startsWith("tel:")
      ? { href: entry.link }
      : { href: entry.link, target: "_blank", rel: "noreferrer" }
    : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
    >
      <Tag
        {...linkProps}
        className="group relative block rounded-2xl border border-white/10 p-6 overflow-hidden transition-all hover:border-aurora-teal/30 hover:-translate-y-0.5 bg-white/[0.02]"
      >
        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-aurora-teal/15 blur-2xl pointer-events-none group-hover:bg-aurora-magenta/20 transition-colors" />
        <div className="relative flex flex-col gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-aurora-teal">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">
              {entry.title}
            </div>
            <div className="mt-1 text-[13px] md:text-sm text-white break-words leading-snug">
              {breakEmail(entry.details)}
            </div>
          </div>
        </div>
      </Tag>
    </motion.div>
  );
};

const Contactpage = () => {
  usePageMeta({
    title: "Contact",
    description:
      "Start a project with Hamza Mehboob. Reach us by email, WhatsApp, LinkedIn, Upwork or Fiverr. We usually reply within 24 hours.",
    path: "/contact",
  });
  return (
    <section className="relative w-full px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[80vw] h-[40vh] rounded-full bg-aurora-teal/15 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-[40vw] h-[40vw] rounded-full bg-aurora-magenta/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow"
          >
            <span className="dot" />
            Get in touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
            className="mt-5 font-jamjuree text-4xl md:text-6xl lg:text-7xl font-semibold text-white leading-tight"
          >
            Let's build something{" "}
            <span className="text-gradient">remarkable</span>.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-5 text-sm md:text-base text-white/65 max-w-xl mx-auto leading-relaxed"
          >
            Now booking consultancy projects and long‑term builds — web,
            mobile and desktop. Pick whichever channel works best for you. We
            usually reply within 24 hours.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            <a
              href="mailto:hmtechnologies.work@gmail.com"
              className="btn-primary"
            >
              hmtechnologies.work@gmail.com
            </a>
            <a
              href="https://wa.me/923158786614"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
            >
              Message on WhatsApp
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="max-w-2xl mx-auto mb-14"
        >
          <ContactForm />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {ContactUs.map((entry, i) => (
            <Tile key={entry.id} entry={entry} idx={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-14 flex justify-center"
        >
          <Link to="/" className="text-sm text-white/55 hover:text-white transition-colors">
            ← Back to home
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Contactpage;
